import { useCallback, useEffect, useState } from "react";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { AxiosError } from "axios";
import { changedEvents } from "@/utils/user.ts";
import { enqueueSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import { maixpanelTrackEventProps } from "@/mixpanel/MixpanelUtils";
import { ieltsOptions, materialOptions } from "@/pages/Materials.tsx";
import { useMe, useUserChangeEventsMutation } from "@/data/user.ts";
import {
  useGetRecordingMutation,
  useSetRecordingMutation,
} from "@/data/agenda.ts";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@/components/ui/Avatar.tsx";
import {
  downloadICS,
  getTypeClass,
  isIntoRangeDates,
  FEEDBACK_EVENT_ID,
  generateRedirectLink,
  SUBTRACT_MINUTES_TO_START_ACCESS_MEET,
} from "@/utils/agenda.ts";
import {
  DownloadICS,
  LoginUserItem,
  FeedbackUserItem,
  EventMaterialItem,
  SelectedEventsProps,
  DefaultErrorResponse,
} from "@/types";
import cn from "classnames";
import bg from "@/assets/details.svg";
import dayjs from "dayjs";
import useAuth from "@/utils/hooks/useAuth";
import VideoIcon from "@/components/icons/VideoIcon.tsx";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import DeleteEvent from "@/components/agenda/desktop/DeleteEvent";
import AgendaPDFIcon from "@/components/icons/agenda/AgendaPDFIcon.tsx";
import PlaceholderUserIcon from "@/components/icons/PlaceholderUserIcon.tsx";
import ChangeEventDropdown from "@/components/agenda/desktop/ChangeEventDropdown.tsx";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";

const BASE_ROUTE = import.meta.env.EDS_BASE_ROUTE;
const STORAGE_ROUTE = import.meta.env.EDS_STORAGE_URL;

interface EventDetailsState {
  id: number;
  date: Date;
  type: string;
  link: string;
  title: string;
  topic: string;
  isPast: boolean;
  endTime: Date;
  livello: string;
  canJoin: boolean;
  duration: number;
  isDeleted: boolean;
  materials: EventMaterialItem[];
  descrizione: string;
  feedbackUser: FeedbackUserItem[];
  nomeVisualizzato: string;
  tutor: {
    first_name: string;
    token: string;
    last_name: string;
    profile_pic: string;
  };
}

interface MaterialsUrlsProps extends EventMaterialItem {
  url: string;
}

interface ActionProps {
  time: number;
  callback: () => void;
}

const EventDetails = () => {
  const { me } = useMe();
  const user = me?.item.user;
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const auth = getToken() as string;
  const isChangeEvents = changedEvents(me!.item);
  const { setGlobalState } = useGlobals();

  const {
    id,
    date,
    link,
    type,
    title,
    topic,
    tutor,
    isPast,
    endTime,
    livello,
    duration,
    materials,
    isDeleted,
    descrizione,
    nomeVisualizzato,
  } = location.state as EventDetailsState;

  const setErrorMessage = useState<string>("")[1];
  const [action] = useState<ActionProps | null>(null);
  const [eventLink, setEventLink] = useState(link);
  const [isDeletable, setIsDeletable] = useState(false);
  const [materialsUrls, setMaterialsUrls] = useState<MaterialsUrlsProps[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<SelectedEventsProps[]>(
    [],
  );

  const { mutate: setRecording, isLoading: isSettingRecording } =
    useSetRecordingMutation();

  const {
    error: isGetRecordingError,
    isLoading: isFetchingRecording,
    mutateAsync: getRecording,
  } = useGetRecordingMutation();

  const {
    data: changeEvents,
    mutate: getChangeEventByUser,
    isLoading: isFetchingChangeEvents,
  } = useUserChangeEventsMutation();

  const isIntoRange = isIntoRangeDates({
    end: endTime,
    start: dayjs(date).clone().toDate(),
    startMinutes: SUBTRACT_MINUTES_TO_START_ACCESS_MEET,
  });

  const createAnchor = (anchorLink: string | null) => {
    if (!user || !anchorLink) return;
    const a = document.createElement("a");

    a.setAttribute("href", anchorLink);
    a.setAttribute("target", "_blank");
    a.click();

    setTimeout(() => {
      a.remove();
    }, 2000);

    setRecording({
      id,
      user: user.token,
    });
  };

  const handleJoin = async () => {
    if (isIntoRange.isAfter && !isIntoRange.isBefore) {
      enqueueSnackbar(t("agenda.too_late_to_join"), {
        variant: "error",
        autoHideDuration: 2000,
      });
    } else {
      createAnchor(generateRedirectLink(id, auth));
      localStorage.setItem(FEEDBACK_EVENT_ID, String(id));
    }
  };

  const handleDownload = () => {
    if (!user) return;
    const currentEvent: DownloadICS = {
      id: id,
      ora: dayjs(date).format("HH:mm"),
      data: dayjs(date).format("YYYY-MM-DD"),
      titolo: title,
      ora_fine: dayjs(endTime).format("HH:mm"),
      link_meet: generateRedirectLink(id, user.token),
      description: descrizione,
    };

    downloadICS(currentEvent, auth);
  };

  const handleFocus = useCallback(() => {
    if (action) {
      const now = Date.now();
      if (action.time <= now) {
        action.callback();
      }
    }
  }, [action]);

  useEffect(() => {
    window.addEventListener("visibilitychange", handleFocus);

    return () => {
      window.removeEventListener("visibilitychange", handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;

    if (
      isPast ||
      isChangeEvents === user.max_change_event ||
      type != "Speaking" ||
      type.toLowerCase() == "one to one"
    ) {
      setSelectedEvents([]);
      return;
    }

    getChangeEventByUser({
      event_id: id,
      livello,
      user_token: user?.token,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isPast, isChangeEvents, type, descrizione, id]);

  useEffect(() => {
    if (!changeEvents) return;
    const dates: SelectedEventsProps[] = [];

    changeEvents.selected_events?.forEach((event) => {
      dates.push({ date: event.data + " " + event.ora, eventId: event.id });
    });

    if (dates.length > 0) {
      setSelectedEvents(dates);
    } else {
      setSelectedEvents([]);
    }
  }, [changeEvents]);

  useEffect(() => {
    if (!materials) return;

    const urls = materials.map((m) => {
      let materialPath;
      if (m?.lesson?.toLowerCase() === "ielts") {
        materialPath =
          BASE_ROUTE +
          "dashboard/materials/list/1/ielts/" +
          ieltsOptions?.find((r) => m.name.toLowerCase().includes(r.type))
            ?.path +
          "/file";
      } else {
        materialPath =
          BASE_ROUTE +
          "dashboard/materials/list/" +
          materialOptions?.find((r) => r.label === m.lesson)?.path +
          "/file";
      }

      return {
        ...m,
        url: materialPath,
      };
    });

    setMaterialsUrls(urls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, BASE_ROUTE]);

  useEffect(() => {
    if (isGetRecordingError) {
      const err = isGetRecordingError as AxiosError<DefaultErrorResponse>;
      setErrorMessage(err.response?.data.message || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetRecordingError]);

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const titleClass = "py-2 px-6 inline-block max-w-max mb-6 rounded";
  const eventType = type ? type.toLowerCase() : "";
  const newTitle =
    eventType === "correction"
      ? "Writing Correction"
      : nomeVisualizzato
      ? nomeVisualizzato
      : title;
  const customStyle = getTypeClass(
    eventType,
    nomeVisualizzato,
    isPast,
    isDeleted,
  );

  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Agenda", me?.item.user as LoginUserItem),
  );

  useEffect(() => {
    const hoursDiff = dayjs(date).diff(dayjs(), "hour");

    if (!isPast && eventType.toLowerCase() === "one to one" && hoursDiff > 12) {
      setIsDeletable(true);
    }
  }, [date, eventType, isPast]);

  const handleFindOrWatchRecording = async () => {
    if (!eventLink) {
      void getRecording(id).then(({ link: recordingLink }) => {
        if (recordingLink) {
          setEventLink(recordingLink);
          createAnchor(recordingLink);
        }
      });
    } else {
      createAnchor(eventLink);
    }
  };

  const PastEventInteractionButton = () => (
    <Button
      data-track-props={mixpanelPropsData}
      data-track-eventtype={
        isFetchingRecording
          ? "Loading"
          : eventLink && eventLink !== "" && eventLink !== null
          ? t("agenda.watch_recording") //"Watch recording"
          : t("agenda.find_recording") //"Find recording"
      }
      variant="default"
      onClick={() => void handleFindOrWatchRecording()}
      isLoading={isFetchingRecording || isSettingRecording}
      className={cn("flex-grow text-base w-1/2", {
        "cursor-pointer": !isIntoRange?.isBefore,
      })}
    >
      {isFetchingRecording || isSettingRecording
        ? "Loading"
        : eventLink && eventLink !== "" && eventLink !== null
        ? t("agenda.watch_recording")
        : t("agenda.find_recording")}
    </Button>
  );

  const EventInteractionButton = () => (
    <Button
      color="primary"
      onClick={() => void handleJoin()}
      className="w-full !text-light text-base font-semibold"
      data-track-props={mixpanelPropsData}
      data-track-eventtype={
        t("agenda.join_class") //"Join class"
      }
    >
      {
        t("agenda.join_class") //"Join class"
      }
    </Button>
  );

  return (
    <DashboardContentLayout
      data-past={`${isPast}`}
      className="flex flex-col gap-4 min-h-full overflow-y-scroll md:h-full w-full p-0"
    >
      <div className="flex flex-col items-center justify-center w-full text-brand relative z-10">
        <div className="flex flex-col pt-6 w-full px-6">
          <h3 className={`${customStyle} ${titleClass} font-semibold text-sm`}>
            {newTitle}
          </h3>

          <div className="flex flex-col mb-4">
            <h5 className="text-light text-sm">{t("agenda.topic")}</h5>

            <p className="font-semibold text-light text-xl">{topic}</p>
          </div>

          <div className="flex flex-col">
            <h5 className="text-light text-sm">{t("agenda.date_label")}</h5>

            <p className="text-xl font-semibold uppercase text-light">
              {dayjs(date).format("ddd DD/MM/YYYY")}
            </p>
          </div>
        </div>
      </div>

      <div
        style={{ backgroundImage: `url(${bg})` }}
        className="flex flex-col relative z-[3] pt-10 bg-no-repeat bg-cover mt-8 h-full"
      >
        <div className="flex flex-row gap-8 items-center justify-center px-6 relative -top-14">
          <div className="flex flex-col items-center gap-y-3">
            <Avatar className="w-36 h-36">
              <AvatarImage
                src={`${STORAGE_ROUTE}/${tutor?.profile_pic}`}
                className="object-cover"
              />

              <AvatarFallback>
                <PlaceholderUserIcon className="h-full w-full text-gray-300" />
              </AvatarFallback>
            </Avatar>
            <p
              className={
                " text-base text-center w-full leading-[100%] font-semibold text-secondary"
              }
            >
              {tutor?.first_name}
            </p>
          </div>

          <div className="flex flex-col gap-4 mt-auto">
            <div className="flex flex-col gap-x-3">
              <h5 className="text-sm text-brand">{t("agenda.time_label")}:</h5>

              <p className="text-xl font-semibold text-brand">
                {dayjs(date).format("HH:mm")}-
                {dayjs(date).add(duration, "minutes").format("HH:mm")}
              </p>
            </div>

            <div className="flex flex-col gap-x-3">
              <h5 className="text-sm text-brand">
                {t("agenda.duration_label")}:
              </h5>

              <Trans
                values={{ duration }}
                i18nKey="agenda.duration_minutes"
                components={{
                  min: <p className="text-xl font-semibold text-brand" />,
                }}
              />
            </div>
          </div>
        </div>

        <div className="relative flex flex-col w-full gap-4 px-4">
          <div className="flex flex-row justify-between gap-x-2 ">
            {isPast ? (
              <PastEventInteractionButton />
            ) : (
              <EventInteractionButton />
            )}

            {!isPast && (
              <Button
                className="flex-shrink-0 text-brand border-brand text-base  w-1/2"
                onClick={() => handleDownload()}
                variant={"outline"}
              >
                {/* Download invite */}
                {t("agenda.download_invite")}
              </Button>
            )}
          </div>

          {isDeletable && (
            <div className="w-full">
              <DeleteEvent eventId={id} />
            </div>
          )}

          {!isPast &&
            (eventType.toLowerCase() == "grammar" ||
              eventType.toLowerCase() == "ielts") && (
              <div className="w-full flex flex-row items-center rounded-lg gap-2 bg-[#eee] px-4 py-2">
                <div>
                  <svg
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="26"
                    viewBox="0 0 25 26"
                  >
                    <path
                      d="M23.4219 10.4158C23.0011 9.97112 22.5658 9.5129 22.4018 9.10999C22.25 8.74093 22.2411 8.12923 22.2321 7.5367C22.2154 6.43518 22.1975 5.18694 21.3393 4.31903C20.481 3.45113 19.2467 3.43307 18.1574 3.41614C17.5714 3.40712 16.9665 3.39809 16.6016 3.2446C16.2042 3.07869 15.75 2.63853 15.3103 2.21304C14.5402 1.46477 13.6652 0.617188 12.5 0.617188C11.3348 0.617188 10.4609 1.46477 9.68973 2.21304C9.25 2.63853 8.79688 3.07869 8.39844 3.2446C8.03571 3.39809 7.42857 3.40712 6.84263 3.41614C5.75335 3.43307 4.51897 3.45113 3.66071 4.31903C2.80246 5.18694 2.79018 6.43518 2.76786 7.5367C2.75893 8.12923 2.75 8.74093 2.59821 9.10999C2.43415 9.51178 1.99888 9.97112 1.57812 10.4158C0.83817 11.1945 0 12.0794 0 13.2576C0 14.4359 0.83817 15.3196 1.57812 16.0995C1.99888 16.5442 2.43415 17.0024 2.59821 17.4053C2.75 17.7743 2.75893 18.386 2.76786 18.9786C2.7846 20.0801 2.80246 21.3283 3.66071 22.1962C4.51897 23.0641 5.75335 23.0822 6.84263 23.0991C7.42857 23.1082 8.03348 23.1172 8.39844 23.2707C8.79576 23.4366 9.25 23.8767 9.68973 24.3022C10.4598 25.0505 11.3348 25.8981 12.5 25.8981C13.6652 25.8981 14.5391 25.0505 15.3103 24.3022C15.75 23.8767 16.2031 23.4366 16.6016 23.2707C16.9665 23.1172 17.5714 23.1082 18.1574 23.0991C19.2467 23.0822 20.481 23.0641 21.3393 22.1962C22.1975 21.3283 22.2154 20.0801 22.2321 18.9786C22.2411 18.386 22.25 17.7743 22.4018 17.4053C22.5658 17.0035 23.0011 16.5442 23.4219 16.0995C24.1618 15.3207 25 14.4359 25 13.2576C25 12.0794 24.1618 11.1957 23.4219 10.4158ZM22.1328 14.8501C21.5982 15.4144 21.0446 15.9979 20.7511 16.7146C20.4699 17.403 20.4576 18.1897 20.4464 18.9515C20.4353 19.7415 20.423 20.5688 20.0759 20.9187C19.7288 21.2685 18.9163 21.2821 18.1306 21.2934C17.3772 21.3046 16.5993 21.3171 15.9185 21.6015C15.2098 21.8983 14.6328 22.4581 14.0748 22.9987C13.5167 23.5393 12.9464 24.0923 12.5 24.0923C12.0536 24.0923 11.4788 23.537 10.9252 22.9987C10.3717 22.4603 9.79018 21.8983 9.08147 21.6015C8.40067 21.3171 7.62277 21.3046 6.86942 21.2934C6.08817 21.2821 5.27009 21.2697 4.92411 20.9187C4.57812 20.5677 4.56473 19.746 4.55357 18.9515C4.54241 18.1897 4.53013 17.403 4.24888 16.7146C3.95536 15.9979 3.40179 15.4144 2.86719 14.8501C2.33259 14.2858 1.78571 13.7091 1.78571 13.2576C1.78571 12.8062 2.33482 12.225 2.86719 11.6652C3.39955 11.1054 3.95536 10.5174 4.24888 9.8007C4.53013 9.11225 4.54241 8.3256 4.55357 7.56379C4.56473 6.77376 4.57701 5.94649 4.92411 5.59662C5.2712 5.24675 6.08371 5.23321 6.86942 5.22192C7.62277 5.21064 8.40067 5.19822 9.08147 4.91381C9.79018 4.61699 10.3672 4.0572 10.9252 3.51659C11.4833 2.97599 12.0536 2.42297 12.5 2.42297C12.9464 2.42297 13.5212 2.97824 14.0748 3.51659C14.6283 4.05494 15.2098 4.61699 15.9185 4.91381C16.5993 5.19822 17.3772 5.21064 18.1306 5.22192C18.9118 5.23321 19.7299 5.24562 20.0759 5.59662C20.4219 5.94762 20.4353 6.76925 20.4464 7.56379C20.4576 8.3256 20.4699 9.11225 20.7511 9.8007C21.0446 10.5174 21.5982 11.1009 22.1328 11.6652C22.6674 12.2295 23.2143 12.8062 23.2143 13.2576C23.2143 13.7091 22.6652 14.2903 22.1328 14.8501ZM11.6071 14.1605V7.8403C11.6071 7.60084 11.7012 7.37119 11.8687 7.20186C12.0361 7.03254 12.2632 6.93741 12.5 6.93741C12.7368 6.93741 12.9639 7.03254 13.1313 7.20186C13.2988 7.37119 13.3929 7.60084 13.3929 7.8403V14.1605C13.3929 14.4 13.2988 14.6296 13.1313 14.799C12.9639 14.9683 12.7368 15.0634 12.5 15.0634C12.2632 15.0634 12.0361 14.9683 11.8687 14.799C11.7012 14.6296 11.6071 14.4 11.6071 14.1605ZM13.8393 18.2235C13.8393 18.4914 13.7607 18.7532 13.6136 18.976C13.4664 19.1987 13.2572 19.3723 13.0125 19.4748C12.7678 19.5773 12.4985 19.6041 12.2387 19.5518C11.9789 19.4996 11.7403 19.3706 11.553 19.1812C11.3657 18.9918 11.2381 18.7505 11.1864 18.4877C11.1348 18.225 11.1613 17.9527 11.2627 17.7052C11.364 17.4578 11.5357 17.2463 11.7559 17.0974C11.9762 16.9486 12.2351 16.8692 12.5 16.8692C12.8552 16.8692 13.1959 17.0119 13.447 17.2659C13.6982 17.5199 13.8393 17.8643 13.8393 18.2235Z"
                      fill="#8065C9"
                    />
                  </svg>
                </div>

                <p className="text-sm">{t("agenda.warning_text")}</p>
              </div>
            )}

          {Boolean(
            !isPast &&
              type == "Speaking" &&
              dayjs().add(1, "hours").isBefore(date) &&
              !title?.toLowerCase().includes("one to one") &&
              descrizione?.toLowerCase() != "mock test",
          ) && (
            <ChangeEventDropdown
              user={user!}
              dates={selectedEvents}
              eventId={id}
              className="col-span-5 py-2"
              eventCount={user!.max_change_event - isChangeEvents}
              isLoadingEvents={isFetchingChangeEvents}
              currentDateEvent={dayjs(date)}
            />
          )}

          <div className="mt-4 flex-col bg-white">
            <p className="mb-1">{t("agenda.material")}: </p>
            {materialsUrls.length === 0 ? (
              <p>{t("agenda.material_not_available")}</p>
            ) : (
              <ul className="flex flex-col gap-y-2 mb-4">
                {materialsUrls.map((r, i) => (
                  <li key={i}>
                    <button
                      onClick={() => {
                        navigate(r.url, {
                          state: {
                            fileType: r.type,
                            tk: r.token,
                            name: r.name,
                          },
                        });
                      }}
                      className="flex flex-nowrap gap-2 !text-left"
                    >
                      {r.type === "video" ? (
                        <VideoIcon className="fill-brand-deep h-6 w-6" />
                      ) : (
                        <AgendaPDFIcon className="fill-brand-deep h-6 w-6" />
                      )}

                      <span>{String(r.name).trim()}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="custom-bg-white" />
    </DashboardContentLayout>
  );
};

export default EventDetails;
